import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOutboundCall,
  getOutboundCalls,
  placeTestCall,
} from '@/services/outbound-calls-service/calls.service';
import {
  OutboundCallsParams,
  PlaceTestCallPayload,
} from '@/services/outbound-calls-service/calls.service.types';
import { OUTBOUND_POLL_INTERVAL_MS } from '@/services/outbound-calls-service/outbound-calls.constants';
import { ReactQueryKeys } from '@/utils/react-query-keys';

export const useOutboundCalls = (params?: OutboundCallsParams) => {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ReactQueryKeys.OUTBOUND_CALLS(params!),
    queryFn: () => getOutboundCalls(params!),
    enabled: !!params?.company_id,
    placeholderData: keepPreviousData,
  });

  return {
    calls: data?.outbound_calls ?? [],
    total: data?.outbound_calls_count ?? 0,
    isLoading,
    isFetching,
    error,
  };
};

/** Polled while the call is on the air: its outcome and the hand-over land when it ends. */
export const useOutboundCall = (id?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.OUTBOUND_CALL(id!),
    queryFn: () => getOutboundCall(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.status === 'live' ? OUTBOUND_POLL_INTERVAL_MS : false,
  });

  return { call: data, isLoading, error };
};

export const usePlaceTestCall = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (payload: PlaceTestCallPayload) => placeTestCall(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_CALLS_ALL() }),
  });

  return { placeTestCall: mutateAsync, isPending };
};
